<template>
    <div v-if="templatesEnabled" style="display: inline-block;">
        <secondary-pill-button
            :disabled="disabled||!contactId"
            @click="toggleDropdown"
            ref="trigger"
        >
            <ui-icon style="font-size: 1rem;">insert_drive_file</ui-icon>
            Insert Template
        </secondary-pill-button>

        <ui-popover
            ref="popover"
            open-on="manual"
            :trigger="$refs.trigger"
            @open="onOpen"
            position="bottom-end"
        >
            <div class="template-dropdown">
                <div class="search-container">
                    <ui-textbox
                        v-model="searchQuery"
                        placeholder="Search templates..."
                        icon="search"
                        :autofocus="true"
                        @input="filterTemplates"
                        style="margin-bottom: 0; padding: 0 10px;"
                    />
                </div>

                <div class="template-list" v-if="!isLoading">
                    <div
                        v-for="template in filteredTemplates"
                        :key="template.id"
                        class="template-item"
                        :class="{ 'disabled': !isTemplateAvailable(template) }"
                        @click="handleTemplateSelected(template)"
                        :title="!isTemplateAvailable(template) ? getUnavailableReason(template) : ''"
                    >
                        <ui-icon>lock</ui-icon>
                        <div class="template-name">{{ template.name }}</div>
                    </div>
                    <div v-if="filteredTemplates.length === 0" class="no-templates">
                        No templates found
                    </div>
                </div>
                <div v-else class="loading-container">
                    <ui-progress-circular v-show="isLoading" :size="32"></ui-progress-circular>
                </div>

                <div class="dropdown-footer" style="display: flex; justify-content: space-between; align-items: center;">
                    <ui-icon-button type="secondary" color="primary" icon="settings" @click.prevent="openManagementModal" disabled></ui-icon-button>
                    <beta-badge style="margin-right: 10px;"></beta-badge>
                </div>
            </div>
        </ui-popover>

        <template-management-modal
            ref="modal"
            @selected="emitTemplateSelected"
        />

        <!-- Data Source Selection Modal -->
        <ui-modal
            ref="dataSourceModal"
            title="Select Data Source"
            size="small"
            :contain-focus="false"
            dismiss-on="close-button esc"
        >
            <div class="data-source-list">
                <p>Multiple options are available for this template. Please select which one to use:</p>
                <div
                    v-for="option in dataSourceOptions"
                    :key="option.id"
                    class="data-source-option"
                    @click="selectDataSourceOption(option)"
                >
                    <div class="option-title">{{ option.title || option.description || `Option ${option.id}` }}</div>
                    <div v-if="option.registration" class="option-detail">{{ option.registration }}</div>
                    <div v-if="option.make && option.model" class="option-detail">{{ option.make }} {{ option.model }}</div>
                </div>
            </div>

            <div slot="footer" style="width: 100%; text-align: right;">
                <ui-button
                    color="default"
                    @click="closeDataSourceModal"
                >
                    Cancel
                </ui-button>
            </div>
        </ui-modal>
    </div>
</template>

<script>
    import TemplateManagementModal from './TemplateManagementModal.vue'
    import SecondaryPillButton from '../elements/SecondaryPillButton.vue'
    import DealerKit from '../../api'
    import BetaBadge from "../../../ts/Elements/BetaBadge.vue";

    export default {
        components: {
            BetaBadge,
            TemplateManagementModal,
            SecondaryPillButton
        },
        props: {
            disabled: {
                type: Boolean,
                default: false
            },
            format: {
                type: String,
                required: true
            },
            contactId: {
                type: Number,
                required: false
            }
        },
        data() {
            return {
                isLoading: false,
                isRendering: false,
                templates: [],
                filteredTemplates: [],
                searchQuery: '',
                availableDataSources: null,
                selectedTemplate: null,
                dataSourceOptions: [],
            }
        },
        computed: {
            templatesEnabled() {
                return window.marque.ui.features.crm.templates
            }
        },
        methods: {
            toggleDropdown() {
                if (!this.disabled && this.contactId) {
                    this.$refs.popover.toggle()
                }
            },
            onOpen() {
                this.searchQuery = ''
                this.loadTemplates()
                this.loadAvailableDataSources()
            },
            async loadTemplates() {
                this.isLoading = true
                try {
                    const dataSources = [
                        'inbox.conversation.lead',
                        'inbox.conversation.vehicle_of_interest',
                        'inbox.conversation.part_exchange_offered',
                        'inbox.conversation.vehicle_reserved',
                        'inbox.conversation.appointment'
                    ].join(',')

                    const response = await DealerKit.templates.getTemplates(1, dataSources, this.format)
                    this.templates = response.data.data
                    this.filterTemplates()
                } catch (error) {
                    console.error('Failed to load templates:', error)
                } finally {
                    this.isLoading = false
                }
            },
            async loadAvailableDataSources() {
                try {
                    const response = await DealerKit.crm.getContactTemplateDataSources(this.contactId)
                    this.availableDataSources = response.data.data
                } catch (error) {
                    console.error('Failed to load available data sources:', error)
                    this.availableDataSources = {}
                }
            },
            filterTemplates() {
                if (!this.searchQuery) {
                    this.filteredTemplates = this.templates
                } else {
                    const query = this.searchQuery.toLowerCase()
                    this.filteredTemplates = this.templates.filter(t =>
                        t.name.toLowerCase().includes(query)
                    )
                }

                window.dispatchEvent(new Event('resize'))
            },
            getDataSourceType(template) {
                const parts = template.data_source.split('.')
                return parts[parts.length - 1]
            },
            isTemplateAvailable(template) {
                if (!this.availableDataSources) {
                    return false
                }
                const dataSourceType = this.getDataSourceType(template)
                const dataSource = this.availableDataSources[dataSourceType]
                return dataSource && dataSource.available
            },
            getUnavailableReason(template) {
                if (!this.availableDataSources) {
                    return 'Checking availability...'
                }
                const dataSourceType = this.getDataSourceType(template)
                const dataSource = this.availableDataSources[dataSourceType]
                if (!dataSource || !dataSource.available) {
                    return `No ${dataSourceType.replace(/_/g, ' ')} available for this conversation`
                }
                return ''
            },
            handleTemplateSelected(template) {
                if (!this.isTemplateAvailable(template) || !this.availableDataSources) {
                    return
                }

                this.$refs.popover.close()

                const dataSourceType = this.getDataSourceType(template)
                const dataSource = this.availableDataSources[dataSourceType]

                this.selectedTemplate = template

                if (dataSource && dataSource.options && dataSource.options.length > 1) {
                    this.dataSourceOptions = dataSource.options
                    this.$refs.dataSourceModal.open()
                } else if (dataSource && dataSource.options && dataSource.options.length === 1) {
                    this.renderTemplate(template.data_source, dataSource.options[0].id)
                }
            },
            closeDataSourceModal() {
                this.$refs.dataSourceModal.close()
            },
            async selectDataSourceOption(option) {
                this.closeDataSourceModal()
                await this.renderTemplate(this.selectedTemplate.data_source, option.id)
            },
            async renderTemplate(dataSource, dataSourceId) {
                this.isRendering = true
                try {
                    const response = await DealerKit.templates.renderTemplate(this.selectedTemplate.id, dataSource, dataSourceId, {
                        headers: {
                            'Accept': this.selectedTemplate.format === 'html' ? 'text/html' : 'text/plain'
                        },
                    })

                    this.emitTemplateSelected({
                        template: this.selectedTemplate,
                        content: response.data
                    })
                } catch (error) {
                    console.error('Failed to render template:', error)
                } finally {
                    this.isRendering = false
                }
            },
            emitTemplateSelected(data) {
                this.$emit('template-selected', data)
            },
            openManagementModal() {
                this.$refs.popover.close()
                this.$refs.modal.open(this.contactId, this.format)
            }
        }
    }
</script>

<style lang="scss" scoped>
    .template-dropdown {
        width: 300px;
        max-height: 400px;
        display: flex;
        flex-direction: column;

        .search-container {
            padding: 8px;
            border-bottom: 1px solid #eee;
        }

        .template-list {
            overflow-y: auto;
            flex-grow: 1;
            padding: 4px 0;
        }

        .template-item {
            padding: 8px 16px;
            cursor: pointer;
            display: flex;
            font-size: 0.8em;

            &:hover:not(.disabled) {
                background-color: #f5f5f5;
            }

            &.disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }

            .ui-icon { width: 35px; font-size: 1.5em; }

            .template-name {
                font-weight: 500;
            }
        }

        .no-templates {
            padding: 16px;
            text-align: center;
            color: #999;
        }

        .loading-container {
            padding: 24px 0;
            display: flex;
            justify-content: center;
        }

        .dropdown-footer {
            border-top: 1px solid #eee;
            padding: 8px;
        }

        .manage-templates-link {
            display: flex;
            align-items: center;
            padding: 8px;
            text-decoration: none;
            color: #2196f3;
            font-weight: 500;

            .ui-icon {
                margin-right: 8px;
                font-size: 1.2rem;
            }

            &:hover {
                background-color: #f5f5f5;
            }
        }
    }

    .data-source-list {
        padding: 1rem 0;
    }

    .data-source-option {
        padding: 1rem;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        margin-bottom: 0.5rem;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            background-color: #f5f5f5;
            border-color: #2196F3;
        }
    }

    .option-title {
        font-weight: 500;
        margin-bottom: 0.25rem;
    }

    .option-detail {
        font-size: 0.875rem;
        color: #666;
    }
</style>
